/**
 * usePendingChanges Hook
 * 
 * React hook for managing pending (unsaved) changes in Library, Template, and Document pages.
 * Provides state management, save/discard operations, and version conflict detection.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  PendingChange,
  PendingChangeItemType,
  getPendingChanges,
  getPendingChangeById,
  addPendingChange,
  removePendingChange,
  removeAllPendingChanges,
  subscribeToPendingChanges,
  isCellModified,
  getModifiedCells,
} from '@/lib/stores/pendingChangesStore';
import { Version } from '@/lib/stores/storeTypes';
import { apiService } from '@/lib/api-functions/api-service';
import { updateObject, objectsStore } from '@/lib/stores/objectsStore';
import { updateTemplate, templatesStore } from '@/lib/stores/templatesStore';
import { updateDocumentItem, documentStore } from '@/lib/stores/documentStore';

export interface VersionConflict {
  itemId: string;
  itemName: string;
  localVersion: Version | null;
  remoteVersion: Version | null;
  pendingChange: PendingChange;
}

export interface UsePendingChangesProps {
  gameId: string | undefined;
  itemType: PendingChangeItemType;
}

export interface UsePendingChangesReturn {
  // State
  pendingChanges: PendingChange[];
  pendingCount: number;
  hasPendingChanges: boolean;
  
  // Cell-level helpers
  isCellModified: (itemId: string, cellKey: string) => boolean;
  getModifiedCells: (itemId: string) => string[];
  hasItemChanges: (itemId: string) => boolean;
  
  // Actions
  trackChange: (
    itemId: string,
    originalVersion: Version | null,
    originalData: any,
    pendingData: any,
    cellKey: string
  ) => void;
  
  // Save operations
  saveItem: (itemId: string) => Promise<{ success: boolean; conflict?: VersionConflict }>;
  saveAllItems: () => Promise<{ success: boolean; conflicts: VersionConflict[] }>;
  forceSaveItem: (itemId: string) => Promise<boolean>;
  
  // Discard operations
  discardItem: (itemId: string) => void;
  discardAllItems: () => void;
  
  // Version conflict handling
  checkVersionConflict: (itemId: string) => Promise<VersionConflict | null>;
}

export function usePendingChanges({
  gameId,
  itemType,
}: UsePendingChangesProps): UsePendingChangesReturn {
  const { toast } = useToast();
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);

  // Subscribe to pending changes store
  useEffect(() => {
    if (!gameId) {
      setPendingChanges([]);
      return;
    }

    // Initial load
    setPendingChanges(getPendingChanges(gameId, itemType));

    // Subscribe to updates
    const unsubscribe = subscribeToPendingChanges((allChanges) => {
      const filtered = allChanges.filter(
        (c) => c.gameId === gameId && c.itemType === itemType
      );
      setPendingChanges(filtered);
    });

    return unsubscribe;
  }, [gameId, itemType]);

  // Computed values
  const pendingCount = pendingChanges.length;
  const hasPendingChangesFlag = pendingCount > 0;

  // Cell-level helpers
  const isCellModifiedFn = useCallback((itemId: string, cellKey: string) => {
    return isCellModified(itemId, cellKey);
  }, []);

  const getModifiedCellsFn = useCallback((itemId: string) => {
    return getModifiedCells(itemId);
  }, []);

  const hasItemChanges = useCallback((itemId: string) => {
    return pendingChanges.some((c) => c.itemId === itemId);
  }, [pendingChanges]);

  // Track a new change
  const trackChange = useCallback(
    (
      itemId: string,
      originalVersion: Version | null,
      originalData: any,
      pendingData: any,
      cellKey: string
    ) => {
      if (!gameId) return;
      addPendingChange(
        itemId,
        itemType,
        gameId,
        originalVersion,
        originalData,
        pendingData,
        cellKey
      );
    },
    [gameId, itemType]
  );

  // Check for version conflicts before saving
  const checkVersionConflict = useCallback(
    async (itemId: string): Promise<VersionConflict | null> => {
      const pendingChange = getPendingChangeById(itemId);
      if (!pendingChange) return null;

      try {
        // Fetch current items from backend and find the specific one
        let remoteVersion: Version | null = null;
        let itemName = '';

        if (itemType === 'library') {
          // Fetch all objects and find the specific one
          const result = await apiService.fetchObjects(pendingChange.gameId);
          if (result.success && result.data) {
            const item = result.data.find((obj: any) => obj.id === itemId);
            if (item) {
              remoteVersion = item.version || null;
              itemName = item.name || 'Unknown item';
            }
          }
        } else if (itemType === 'template') {
          // Fetch all templates and find the specific one
          const result = await apiService.fetchBlueprints(pendingChange.gameId);
          if (result.success && result.data) {
            const item = result.data.find((bp: any) => bp.id === itemId);
            if (item) {
              remoteVersion = item.version || null;
              itemName = item.name || 'Unknown template';
            }
          }
        } else if (itemType === 'document') {
          // Fetch all documents and find the specific one
          const result = await apiService.fetchDocuments(pendingChange.gameId);
          if (result.success && result.data) {
            const item = result.data.find((doc: any) => doc.id === itemId);
            if (item) {
              remoteVersion = item.version || null;
              itemName = item.name || 'Unknown document';
            }
          }
        }

        // Compare versions
        const localVersion = pendingChange.originalVersion;
        
        if (remoteVersion && localVersion) {
          const isRemoteNewer = compareVersions(remoteVersion, localVersion) > 0;
          
          if (isRemoteNewer) {
            return {
              itemId,
              itemName,
              localVersion,
              remoteVersion,
              pendingChange,
            };
          }
        }

        return null;
      } catch (error) {
        console.error('[usePendingChanges] Error checking version:', error);
        return null;
      }
    },
    [itemType]
  );

  // Save a single item
  const saveItem = useCallback(
    async (itemId: string): Promise<{ success: boolean; conflict?: VersionConflict }> => {
      const pendingChange = getPendingChangeById(itemId);
      if (!pendingChange) {
        return { success: false };
      }

      // Check for version conflict first
      const conflict = await checkVersionConflict(itemId);
      if (conflict) {
        return { success: false, conflict };
      }

      // No conflict, proceed with save
      return { success: await forceSaveItem(itemId) };
    },
    [checkVersionConflict]
  );

  // Force save an item (bypasses version check - used after user confirms)
  const forceSaveItem = useCallback(
    async (itemId: string): Promise<boolean> => {
      const pendingChange = getPendingChangeById(itemId);
      if (!pendingChange) {
        return false;
      }

      try {
        if (itemType === 'library') {
          // Save library object
          await updateObject(
            pendingChange.pendingData,
            'patch',
            'Local changes saved',
            toast
          );
        } else if (itemType === 'template') {
          // Save template
          await updateTemplate(pendingChange.pendingData, toast);
        } else if (itemType === 'document') {
          // Save document
          await updateDocumentItem(pendingChange.pendingData, toast);
        }

        // Remove from pending changes on success
        removePendingChange(itemId);

        toast({
          title: 'Success',
          description: `Changes saved successfully`,
        });

        return true;
      } catch (error) {
        console.error('[usePendingChanges] Error saving item:', error);
        toast({
          title: 'Error',
          description: 'Failed to save changes',
          variant: 'destructive',
        });
        return false;
      }
    },
    [itemType, toast]
  );

  // Save all items
  const saveAllItems = useCallback(
    async (): Promise<{ success: boolean; conflicts: VersionConflict[] }> => {
      const conflicts: VersionConflict[] = [];
      let allSuccess = true;

      for (const pendingChange of pendingChanges) {
        // Check for conflict
        const conflict = await checkVersionConflict(pendingChange.itemId);
        if (conflict) {
          conflicts.push(conflict);
          allSuccess = false;
          continue;
        }

        // No conflict, save
        const saved = await forceSaveItem(pendingChange.itemId);
        if (!saved) {
          allSuccess = false;
        }
      }

      if (conflicts.length === 0 && allSuccess) {
        toast({
          title: 'Success',
          description: `All ${pendingChanges.length} items saved successfully`,
        });
      } else if (conflicts.length > 0) {
        toast({
          title: 'Version Conflicts',
          description: `${conflicts.length} item(s) have newer versions on the server`,
          variant: 'destructive',
        });
      }

      return { success: allSuccess && conflicts.length === 0, conflicts };
    },
    [pendingChanges, checkVersionConflict, forceSaveItem, toast]
  );

  // Discard a single item's changes
  const discardItem = useCallback(
    (itemId: string) => {
      const pendingChange = getPendingChangeById(itemId);
      if (!pendingChange) return;

      // Restore original data to the store
      if (itemType === 'library') {
        const currentData = objectsStore.getData();
        const updatedData = currentData.map((item) =>
          item.id === itemId ? pendingChange.originalData : item
        );
        objectsStore.setData(updatedData);
      } else if (itemType === 'template') {
        const currentData = templatesStore.getData();
        const updatedData = currentData.map((item) =>
          item.id === itemId ? pendingChange.originalData : item
        );
        templatesStore.setData(updatedData);
      } else if (itemType === 'document') {
        const currentData = documentStore.getData();
        const updatedData = currentData.map((item) =>
          item.id === itemId ? pendingChange.originalData : item
        );
        documentStore.setData(updatedData);
      }

      // Remove from pending changes
      removePendingChange(itemId);

      toast({
        title: 'Changes Discarded',
        description: 'Local changes have been discarded',
      });
    },
    [itemType, toast]
  );

  // Discard all changes
  const discardAllItems = useCallback(() => {
    if (!gameId) return;

    // Restore all original data
    for (const pendingChange of pendingChanges) {
      if (itemType === 'library') {
        const currentData = objectsStore.getData();
        const updatedData = currentData.map((item) =>
          item.id === pendingChange.itemId ? pendingChange.originalData : item
        );
        objectsStore.setData(updatedData);
      } else if (itemType === 'template') {
        const currentData = templatesStore.getData();
        const updatedData = currentData.map((item) =>
          item.id === pendingChange.itemId ? pendingChange.originalData : item
        );
        templatesStore.setData(updatedData);
      } else if (itemType === 'document') {
        const currentData = documentStore.getData();
        const updatedData = currentData.map((item) =>
          item.id === pendingChange.itemId ? pendingChange.originalData : item
        );
        documentStore.setData(updatedData);
      }
    }

    // Remove all pending changes
    removeAllPendingChanges(gameId, itemType);

    toast({
      title: 'All Changes Discarded',
      description: `Discarded changes to ${pendingChanges.length} item(s)`,
    });
  }, [gameId, itemType, pendingChanges, toast]);

  return {
    pendingChanges,
    pendingCount,
    hasPendingChanges: hasPendingChangesFlag,
    isCellModified: isCellModifiedFn,
    getModifiedCells: getModifiedCellsFn,
    hasItemChanges,
    trackChange,
    saveItem,
    saveAllItems,
    forceSaveItem,
    discardItem,
    discardAllItems,
    checkVersionConflict,
  };
}

/**
 * Compare two versions
 * Returns: positive if v1 > v2, negative if v1 < v2, 0 if equal
 */
function compareVersions(v1: Version, v2: Version): number {
  // Handle both old (number) and new (object) version formats
  const getVersionNumber = (v: Version | number): { major: number; minor: number; patch: number } => {
    if (typeof v === 'number') {
      return { major: v, minor: 0, patch: 0 };
    }
    if (typeof v === 'object' && v !== null) {
      // New format: { major, minor, patch }
      if ('major' in v) {
        return { major: v.major || 0, minor: v.minor || 0, patch: v.patch || 0 };
      }
      // Old format: { version, changes }
      if ('version' in v && typeof v.version === 'number') {
        return { major: v.version, minor: 0, patch: 0 };
      }
    }
    return { major: 0, minor: 0, patch: 0 };
  };

  const ver1 = getVersionNumber(v1);
  const ver2 = getVersionNumber(v2);

  if (ver1.major !== ver2.major) return ver1.major - ver2.major;
  if (ver1.minor !== ver2.minor) return ver1.minor - ver2.minor;
  return ver1.patch - ver2.patch;
}

